// Toasty (TO-1..TO-5): stos max 3, auto-znikanie, pauza na hover, akcja opcjonalna.
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { IconCheckCircle, IconAlertCircle, IconInfo, IconAlertTriangle } from "./icons";

type ToastVariant = "success" | "danger" | "info" | "warning";

type ToastInput = {
  variant?: ToastVariant;
  title: string;
  body?: string;
};

type ToastItem = ToastInput & { id: number; variant: ToastVariant; leaving?: boolean };

type ToastApi = {
  toast: (t: ToastInput) => void;
  success: (title: string, body?: string) => void;
  error: (title: string, body?: string) => void;
  info: (title: string, body?: string) => void;
  warning: (title: string, body?: string) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

const DURATION: Record<ToastVariant, number> = {
  success: 4000,
  info: 4000,
  warning: 8000,
  danger: 8000,
};

const ICONS: Record<ToastVariant, typeof IconInfo> = {
  success: IconCheckCircle,
  danger: IconAlertCircle,
  info: IconInfo,
  warning: IconAlertTriangle,
};

const MAX = 3;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  const idRef = useRef(0);

  const remove = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setItems((prev) => prev.filter((t) => t.id !== id));
      return;
    }
    setItems((prev) => prev.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), 220);
  }, []);

  const schedule = useCallback(
    (id: number, ms: number) => {
      const existing = timers.current.get(id);
      if (existing) clearTimeout(existing);
      timers.current.set(id, setTimeout(() => remove(id), ms));
    },
    [remove],
  );

  const toast = useCallback(
    (input: ToastInput) => {
      const variant = input.variant ?? "info";
      const id = ++idRef.current;
      const item: ToastItem = { ...input, variant, id };
      setItems((prev) => {
        const next = [item, ...prev];
        return next.slice(0, MAX);
      });
      schedule(id, DURATION[variant]);
    },
    [schedule],
  );

  const api = useMemo<ToastApi>(
    () => ({
      toast,
      success: (title, body) => toast({ variant: "success", title, body }),
      error: (title, body) => toast({ variant: "danger", title, body }),
      info: (title, body) => toast({ variant: "info", title, body }),
      warning: (title, body) => toast({ variant: "warning", title, body }),
    }),
    [toast],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div className="toast-stack" aria-live="polite">
          {items.map((t) => {
            const Icon = ICONS[t.variant];
            return (
              <div
                key={t.id}
                className={`toast toast--${t.variant}${t.leaving ? " is-leaving" : ""}`}
                role={t.variant === "danger" ? "alert" : "status"}
                onMouseEnter={() => {
                  const timer = timers.current.get(t.id);
                  if (timer) clearTimeout(timer);
                }}
                onMouseLeave={() => schedule(t.id, DURATION[t.variant])}
              >
                <Icon className="toast__icon" />
                <p className="toast__title">{t.title}</p>
                <button
                  type="button"
                  className="toast__close"
                  aria-label="Zamknij powiadomienie"
                  onClick={() => remove(t.id)}
                >
                  ×
                </button>
                {t.body ? <p className="toast__body">{t.body}</p> : null}
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}
